<script setup lang="ts">
  import { onMounted } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import { VueDraggable } from 'vue-draggable-plus';
  import { SquarePlus, SquareMinus, Search, Check } from 'lucide-vue-next';
  import { useElementProperties } from '../../../model/useElementProperties';
  import { useDropdownAlternativesModal } from '../../../model/useDropdownAlternativesModal';
  import { ALTERNATIVES_DRAGGABLE_OPTIONS } from '../../../lib/alternatives-draggable-options';
  import AlternativeDragHandle from './AlternativeDragHandle.vue';
  import type { DropdownListType } from '../../../model/form.types';
  import {
    Input,
    SimpleLoading,
    UiModalHeader,
    ColoredButton,
    ThemeProvider,
    CustomCheckbox,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
  } from '@/shared/ui';

  interface Props {
    elementId: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const { selectedElement, updateElement } = useElementProperties(() => props.elementId);

  const {
    listType,
    localAlternatives,
    searchQuery,
    remoteItems,
    isLoadingRemote,
    totalCount,
    selectedRemoteIds,
    allSelected,
    initFromElement,
    addAlternative,
    removeAlternative,
    updateAlternative,
    fetchRemoteItems,
    toggleItem,
    toggleSelectAll,
    onListTypeChange,
    save,
  } = useDropdownAlternativesModal({ selectedElement, updateElement });

  function handleSave() {
    save();
    emit('close');
  }

  function handleClose() {
    emit('close');
  }

  onMounted(() => {
    initFromElement();
  });
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="flex flex-col bg-white-01 rounded-2xl w-[508px] max-h-[90vh] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
  >
    <ThemeProvider class="flex flex-col max-h-[90vh]">
      <!-- Header -->
      <UiModalHeader
        class="px-6 py-3 border-b border-[var(--color-gray-02)] shrink-0"
        title="Alternativas Dropdown"
        title-size="text-sm"
        @close="handleClose"
      />

      <!-- Tipo de lista -->
      <div class="px-6 pt-6 pb-8 border-b border-[var(--color-gray-02)] shrink-0">
        <label class="block text-sm font-semibold text-record-header-text-02 mb-2">Tipo de lista:</label>
        <Select
          :model-value="listType"
          @update:model-value="(val) => onListTypeChange(val as DropdownListType)"
        >
          <SelectTrigger class="w-full h-11">
            <SelectValue placeholder="Selecione o tipo" />
          </SelectTrigger>
          <SelectContent class="z-[9999]">
            <SelectItem value="LIVRE">
              Livre
            </SelectItem>
            <SelectItem value="CARGOS">
              Cargos
            </SelectItem>
            <SelectItem value="DEPARTAMENTOS">
              Departamentos
            </SelectItem>
            <SelectItem value="USUARIOS">
              Usuários
            </SelectItem>
            <SelectItem value="MEMBROS">
              Membros
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <!-- Alternatives section -->
      <div class="flex-1">
        <p class="px-6 pt-6 pb-3 text-sm font-semibold text-[var(--color-gray-07)]">
          Alternativas
        </p>

        <!-- LIVRE mode: white card with fixed-height list + add button -->
        <div
          v-if="listType === 'LIVRE'"
          class="mx-6 mb-4"
        >
          <div class="bg-white-01 border border-[var(--color-gray-02)] rounded-2xl p-6">
            <VueDraggable
              v-model="localAlternatives"
              v-bind="ALTERNATIVES_DRAGGABLE_OPTIONS"
              class="space-y-3 h-64 overflow-y-auto pr-2 mb-3"
            >
              <div
                v-for="(alternative, index) in localAlternatives"
                :key="alternative.id"
                class="flex items-center gap-4"
              >
                <AlternativeDragHandle />
                <Input
                  :model-value="alternative.value"
                  :placeholder="`Opção ${index + 1}`"
                  class="flex-1 h-11 focus-visible:ring-0"
                  @update:model-value="(val) => updateAlternative(index, String(val))"
                />
                <button
                  type="button"
                  class="shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                  :disabled="localAlternatives.length <= 1"
                  @click="removeAlternative(index)"
                >
                  <SquareMinus
                    :size="24"
                    class="text-[var(--theme-secondary-ink)]"
                  />
                </button>
              </div>
            </VueDraggable>
            <button
              type="button"
              class="w-full h-[42px] bg-[var(--theme-secondary)] rounded-lg flex items-center justify-center hover:opacity-90 transition-opacity"
              @click="addAlternative"
            >
              <SquarePlus
                :size="20"
                color="white"
              />
            </button>
          </div>
        </div>

        <!-- Dynamic mode (CARGOS, DEPARTAMENTOS, USUARIOS, MEMBROS) -->
        <div
          v-else
          class="mx-6 mb-4 flex flex-col gap-2"
        >
          <!-- Search input fora do card -->
          <div class="relative">
            <Search
              :size="20"
              class="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--color-gray-03)]"
            />
            <Input
              v-model="searchQuery"
              placeholder="Buscar"
              class="pl-10 h-[42px]"
              @update:model-value="fetchRemoteItems"
            />
          </div>

          <!-- Select-all fora do card -->
          <div class="flex items-center gap-3 px-2 py-3">
            <CustomCheckbox
              id="select-all"
              :checked="allSelected"
              @change="toggleSelectAll"
            />
            <label
              for="select-all"
              class="text-sm font-medium text-[var(--color-gray-07)] cursor-pointer select-none"
            >
              Selecionar todas as alternativas
            </label>
          </div>

          <!-- Card com lista de itens em altura fixa -->
          <div class="bg-white-01 border border-[var(--color-gray-02)] rounded-2xl p-6">
            <div
              v-if="isLoadingRemote"
              class="flex justify-center items-center h-48"
            >
              <SimpleLoading />
            </div>

            <div
              v-else
              class="flex flex-col gap-3 h-48 overflow-y-auto pr-2"
            >
              <div
                v-for="item in remoteItems"
                :key="item.id"
                class="flex items-center gap-3 cursor-pointer"
                @click="toggleItem(item)"
              >
                <CustomCheckbox
                  :id="`item-${item.id}`"
                  :checked="selectedRemoteIds.has(item.id)"
                  @click.stop="toggleItem(item)"
                />
                <span class="text-sm font-medium text-[var(--color-gray-07)] select-none">
                  {{ item.value }}
                </span>
              </div>

              <div
                v-if="remoteItems.length === 0"
                class="flex justify-center items-center h-full text-sm text-[var(--color-gray-03)]"
              >
                Nenhum item encontrado
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Total -->
      <div class="px-10 pt-3 pb-6 border-b border-[var(--color-gray-02)] shrink-0">
        <p class="text-sm text-[var(--color-gray-07)]">
          <template v-if="listType === 'LIVRE'">
            Total de Alternativas: {{ totalCount }}
          </template>
          <template v-else>
            Total de Alternativas selecionadas: {{ totalCount }}
          </template>
        </p>
      </div>

      <!-- Footer -->
      <div class="px-6 py-8 flex justify-end gap-6 shrink-0">
        <ColoredButton
          text="Cancelar"
          outlined
          @click="handleClose"
        />
        <ColoredButton
          text="Salvar"
          :icon="Check"
          :disabled="totalCount === 0"
          @click="handleSave"
        />
      </div>
    </ThemeProvider>
  </VueFinalModal>
</template>
